import { parseDateString, parseFmtDate } from "@saleor/utils/date";
import { useFormik } from "formik";
import moment from "moment";
import { useIntl } from "react-intl";
import * as Yup from "yup";

const getOrderSchema = () => {
  const intl = useIntl();
  const textError = intl.formatMessage({ defaultMessage: "โปรดระบุข้อมูล" });
  const textDateError = intl.formatMessage({
    defaultMessage:
      "กรุณาเปลี่ยน “Request Date” (ไม่สามารถใส่วันที่ปัจจุบัน หรือวันที่ผ่านมาแล้วได้)"
  });
  const textEtdError = intl.formatMessage({
    defaultMessage:
      "กรุณาเปลี่ยน “ETA” (ไม่สามารถใส่วันที่ก่อนหรือวันเดียวกับ ETD ได้)"
  });
  return Yup.object().shape({
    shipTo: Yup.string().required(textError),
    billTo: Yup.string().required(textError),
    poNo: Yup.string().required(textError),
    isRequestDateChange: Yup.boolean().default(false),
    isEtdChange: Yup.boolean().default(false),
    isEtaChange: Yup.boolean().default(false),
    requestDate: Yup.date()
      .transform(parseDateString)
      .when("isRequestDateChange", {
        is: true,
        then: Yup.date()
          .typeError(textError)
          .min(new Date(), textDateError)
          .required(textError)
      }),
    placeOfDelivery: Yup.string().required(textError),
    portOfDischarge: Yup.string().required(textError),
    portOfLoading: Yup.string().required(textError),
    etd: Yup.date()
      .transform(parseDateString)
      .typeError(textError)
      .required(textError),
    eta: Yup.date()
      .transform(parseDateString)
      .when(["isEtdChange", "isEtaChange"], {
        is: (isEtdChange, isEtaChange) => isEtdChange || isEtaChange,
        then: Yup.date()
          .typeError(textError)
          .test("greater-than-etd", textEtdError, function(value) {
            const etd = this.resolve(Yup.ref("etd"));
            return value === null || value === undefined || value > etd;
          })
          .required(textError)
      }),
    payer: Yup.string().required(textError)
  });
};

const getShipToCode = dataExportOrder => {
  let _shipToCode = "";
  // eslint-disable-next-line chai-friendly/no-unused-expressions
  dataExportOrder?.orderPartners?.forEach(orderPartner => {
    if (orderPartner?.partnerRole === "WE") {
      _shipToCode = orderPartner?.partnerNo || "";
    }
  });
  return _shipToCode;
};

const getShipToName = dataExportOrder => {
  let _shipToName = "";
  // eslint-disable-next-line chai-friendly/no-unused-expressions
  dataExportOrder?.orderPartners?.forEach(orderPartner => {
    if (orderPartner?.partnerRole === "WE") {
      _shipToName = `${orderPartner?.address[0]?.nameForPartners ||
        ""}\n ${orderPartner?.address[0]?.street || ""} ${orderPartner
        ?.address[0]?.strSuppl1 || ""} ${orderPartner?.address[0]?.strSuppl2 ||
        ""} ${orderPartner?.address[0]?.strSuppl3 || ""} ${orderPartner
        ?.address[0]?.location || ""} ${orderPartner?.address[0]?.district ||
        ""} ${orderPartner?.address[0]?.city || ""} ${orderPartner?.address[0]
        ?.postCode || ""}`;
    }
  });
  return _shipToName;
};

const getBillToPartyName = dataExportOrder => {
  const _billToPartyName = dataExportOrder?.orderPartners?.find(
    item => item.partnerRole === "RE"
  );
  return `${_billToPartyName?.partnerNo || ""} - ${_billToPartyName?.address[0]
    ?.nameForPartners || ""}\n ${_billToPartyName?.address[0]?.street ||
    ""} ${_billToPartyName?.address[0]?.strSuppl1 || ""} ${_billToPartyName
    ?.address[0]?.strSuppl2 || ""} ${_billToPartyName?.address[0]?.strSuppl3 ||
    ""} ${_billToPartyName?.address[0]?.location || ""} ${_billToPartyName
    ?.address[0]?.district || ""} ${_billToPartyName?.address[0]?.city ||
    ""} ${_billToPartyName?.address[0]?.postCode || ""}`;
};

const getInitShipTo = dataExportOrder =>
  `${getShipToCode(dataExportOrder)} - ${getShipToName(dataExportOrder)}`;

const getInitBillTo = dataExportOrder => getBillToPartyName(dataExportOrder);

export const getPayer = dataExportOrder => {
  let _payer = "";
  // eslint-disable-next-line chai-friendly/no-unused-expressions
  dataExportOrder?.orderPartners?.forEach(orderPartner => {
    if (orderPartner?.partnerRole === "RG") {
      _payer = `${orderPartner?.partnerNo} - ${orderPartner?.payerName}`;
    }
  });
  return _payer;
};

export const getAuthor = dataExportOrder => {
  let _author = "";
  // eslint-disable-next-line chai-friendly/no-unused-expressions
  dataExportOrder?.orderPartners?.forEach(orderPartner => {
    if (orderPartner?.partnerRole === "AU") {
      _author = orderPartner?.partnerNo;
    }
  });
  return _author;
};

export const getDataOrderTextByCode = (exportOrder, code) => {
  const _item = exportOrder?.orderText?.find(item => item?.textId === code);
  let _text = "";
  // eslint-disable-next-line chai-friendly/no-unused-expressions
  _item?.textLine?.forEach(text =>
    _text === "" ? (_text += text.text) : (_text += `\n${text.text}`)
  );

  return _text;
};

export const getDataOrderTextByCodeAndItemNo = (exportOrder, code, itemNo) => {
  const _item = exportOrder?.orderText?.find(
    item => item?.textId === code && +item?.itemNo === +itemNo
  );
  let _text = "";
  // eslint-disable-next-line chai-friendly/no-unused-expressions
  _item?.textLine?.forEach(text =>
    _text === "" ? (_text += text.text) : (_text += `\n${text.text}`)
  );
  return _text;
};

export const getInitHeaderValue = dataExportOrder => ({
  shipTo: getInitShipTo(dataExportOrder),
  billTo: getInitBillTo(dataExportOrder),
  poNo: dataExportOrder?.poNo || "",
  poDate: dataExportOrder?.poDate
    ? moment(dataExportOrder?.poDate, "DD/MM/YYYY").format("YYYY-MM-DD")
    : "",
  isRequestDateChange: false,
  isEtdChange: false,
  isEtaChange: false,
  requestDate: dataExportOrder?.requestDate
    ? moment(dataExportOrder?.requestDate, "DD/MM/YYYY").format("YYYY-MM-DD")
    : "",
  refPiNo: dataExportOrder?.refPiNo || "",
  usage: dataExportOrder?.usage || "",
  unloadingPoint: dataExportOrder?.unloadingPoint || "",
  placeOfDelivery: dataExportOrder?.incoterms2 || "",
  portOfDischarge:
    getDataOrderTextByCodeAndItemNo(dataExportOrder, "Z013", 0) || "",
  portOfLoading:
    getDataOrderTextByCodeAndItemNo(dataExportOrder, "Z014", 0) || "",
  noOfContainers:
    getDataOrderTextByCodeAndItemNo(dataExportOrder, "Z022", 0) || "",
  uom: getDataOrderTextByCodeAndItemNo(dataExportOrder, "Z019", 0) || "",
  gwUom: getDataOrderTextByCodeAndItemNo(dataExportOrder, "ZK35", 0) || "",
  etd:
    parseFmtDate(getDataOrderTextByCodeAndItemNo(dataExportOrder, "Z038", 0)) ||
    "",
  eta:
    parseFmtDate(getDataOrderTextByCodeAndItemNo(dataExportOrder, "Z066", 0)) ||
    "",
  dlcExpiryDate:
    parseFmtDate(getDataOrderTextByCodeAndItemNo(dataExportOrder, "Z223", 0)) ||
    "",
  dlcNo: getDataOrderTextByCodeAndItemNo(dataExportOrder, "Z222", 0) || "",
  dlcLatestDeliveryDate:
    parseFmtDate(getDataOrderTextByCodeAndItemNo(dataExportOrder, "Z224", 0)) ||
    "",
  description: dataExportOrder?.description || "",
  payer: getPayer(dataExportOrder) || "",
  paymentInstruction:
    getDataOrderTextByCodeAndItemNo(dataExportOrder, "Z012", 0) || "",
  remark: getDataOrderTextByCodeAndItemNo(dataExportOrder, "Z016", 0) || "",
  productionInformation:
    getDataOrderTextByCodeAndItemNo(dataExportOrder, "ZK08", 0) || "",
  internalCommentToWarehouse:
    getDataOrderTextByCodeAndItemNo(dataExportOrder, "Z001", 0) || "",
  shippingMark:
    getDataOrderTextByCodeAndItemNo(dataExportOrder, "Z004", 0) || ""
});

export const getFormik = dataExportOrder =>
  useFormik({
    initialValues: getInitHeaderValue(dataExportOrder),
    onSubmit: () => {
      //
    },
    validationSchema: getOrderSchema()
  });
